import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getSessionId } from '../../utils/agents';
import { database } from '../../utils/database';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import { hasPermission } from "../../utils/permissions";
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal";
import Button from "../Common/Button";
import { marked } from 'marked';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';

const { 
  FiSend, 
  FiArrowLeft, 
  FiRefreshCw, 
  FiArrowDown, 
  FiCopy, 
  FiCheck, 
  FiInfo, 
  FiX, 
  FiMail,
  FiAlertCircle,
  FiPaperclip
} = FiIcons;

// Function to parse Markdown with code highlighting and sanitization
const parseMarkdown = (text, t) => {
  if (!text) return '';

  marked.setOptions({
    highlight: function (code, lang) {
      try {
        return hljs.highlightAuto(code).value;
      } catch (e) {
        console.error('Error highlighting code:', e);
        return code;
      }
    },
    gfm: true,
    breaks: true, // Disable automatic line breaks to match original behavior
  });

  let renderedHtml = text;
  try {
    if (text.startsWith('<') && text.endsWith('>') && /<[^>]+>/.test(text)) {
      renderedHtml = text;
    } else {
      renderedHtml = marked.parse(text);
    }

    // Remove surrounding <p> tags and extra whitespace
    renderedHtml = renderedHtml.trim();
    renderedHtml = renderedHtml.replace(/^<p>(.*?)<\/p>$/, '$1');
  } catch (e) {
    console.error('Error parsing Markdown:', e);
    renderedHtml = `<div>${text}</div>`;
  }

  renderedHtml = DOMPurify.sanitize(renderedHtml, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allowfullscreen']
  });

  return renderedHtml;
};

// Helper function to log ONLY user messages to Supabase
const logUserMessage = async ({ userId, agentId, content }) => {
  try {
    console.log('Logging user message to Supabase:', { userId, agentId, content: content.substring(0, 50) + (content.length > 50 ? '...' : '') });
    const { data, error } = await supabase
      .from('messages_a1b2c3d4e5')
      .insert([{ user_id: userId, agent_id: agentId, content, created_at: new Date().toISOString() }]);
    
    if (error) {
      console.error('Error saving user message to database:', error);
    } else {
      console.log('User message successfully logged to Supabase');
    }
  } catch (error) {
    console.error('Error in logUserMessage:', error);
  }
};

const ChatInterface = ({ agent, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [agentImage, setAgentImage] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showAgentDetails, setShowAgentDetails] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [chatState, setChatState] = useState('idle'); // idle | executing | cancelled
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    attachments: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [defaultRequestEmail, setDefaultRequestEmail] = useState('support@prosomo.com');
  const [agentRequestEmail, setAgentRequestEmail] = useState(null);
  
  const listRef = useRef(null);
  const autoScrollRef = useRef(true);
  const messagesEndRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const inflightKeyRef = useRef(null);
  const messagesRef = useRef(messages);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const { user } = useAuth();
  const t = useTranslation(user?.language);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchDefaultRequestEmail = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings_a1b2c3d4e5')
          .select('value')
          .eq('key', 'default_request_email')
          .single();

        if (!error && data) {
          setDefaultRequestEmail(data.value);
        }
      } catch (error) {
        console.error('Error fetching default request email:', error);
      }
    };

    fetchDefaultRequestEmail();
  }, []);

  useEffect(() => {
    const fetchAgentRequestEmail = async () => {
      try {
        const { data, error } = await supabase
          .from('ai_agents_a1b2c3d4e5')
          .select('request_email')
          .eq('id', agent.id)
          .single();

        if (!error && data && data.request_email) {
          setAgentRequestEmail(data.request_email);
        }
      } catch (error) {
        console.error('Error fetching agent request email:', error);
      }
    };

    if (agent?.id) {
      fetchAgentRequestEmail();
    }
  }, [agent?.id]);

  useEffect(() => {
    const fetchAgentImage = async () => {
      try {
        const { data, error } = await supabase
          .from('agent_images_a1b2c3d4e5')
          .select('image_url')
          .eq('agent_id', agent.id)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') {
            console.error('Error fetching agent image:', error);
          }
          return;
        }

        if (data && data.image_url) {
          setAgentImage(data.image_url);
        }
      } catch (error) {
        console.error('Error in agent image fetch:', error);
      }
    };

    fetchAgentImage();

    const channel = supabase
      .channel(`agent_image_${agent.id}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'agent_images_a1b2c3d4e5', filter: `agent_id=eq.${agent.id}` },
        (payload) => {
          if (payload.new && payload.new.image_url) {
            setAgentImage(payload.new.image_url);
          } else if (payload.eventType === 'DELETE') {
            setAgentImage(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [agent.id]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight;
      autoScrollRef.current = atBottom;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    
    if (autoScrollRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const currentSessionId = getSessionId(user.id, agent.id);
    setSessionId(currentSessionId);

    const sessionKey = `chat_${agent.id}_${user.id}`;
    const savedMessages = sessionStorage.getItem(sessionKey);
    
    if (savedMessages) {
      const parsedMessages = JSON.parse(savedMessages);
      setMessages(parsedMessages);
      
      setTimeout(() => {
        autoScrollRef.current = true;
        if (listRef.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const welcomeMessage = {
        id: Date.now(),
        text: `${t('chatInterface.welcome', 'Welcome to')} ${agent.name}! ${t('chatInterface.howCanIHelp', 'How can I help you today?')}`,
        sender: 'agent',
        timestamp: new Date().toISOString()
      };
      
      setMessages([welcomeMessage]);
      sessionStorage.setItem(sessionKey, JSON.stringify([welcomeMessage]));
      
      setTimeout(() => {
        autoScrollRef.current = true;
        if (listRef.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [agent.id, user.id, agent.name]);

  const saveMessagesToSession = (newMessages) => {
    const sessionKey = `chat_${agent.id}_${user.id}`;
    sessionStorage.setItem(sessionKey, JSON.stringify(newMessages));
  };

  const persistAndSetIfMounted = (list) => {
    saveMessagesToSession(list);
    if (mountedRef.current) setMessages(list);
  };

  const enqueueUserMessage = (text) => {
    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    
    const updated = [...messagesRef.current, userMessage];
    persistAndSetIfMounted(updated);
    
    return { updatedMessages: updated, userMessage };
  };

  const handleCopyMessage = async (messageText, messageId) => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleCancelExecution = async () => {
    if (abortRef.current) {
      abortRef.current.abort();
      setChatState('cancelled');
      setIsTyping(false);
      setLoading(false);      
    }    
  };

  const sendToAgentOnce = async ({ text }) => {
    // fixed \n or invalid characters when submitting very long messages
    const key = btoa(`${sessionId}::${agent.id}::${text}`);
    if (inflightKeyRef.current === key) return;
    inflightKeyRef.current = key;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsTyping(true);
    setChatState('executing');

    try {
      const payload = {
        chatInput: text,
        sessionId,
        userId: user.id,
        userName: user.fullName,
        lang: user.language || 'fr'
      };

      const res = await fetch(agent.webhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': key
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data.execution_id) {
        setExecutionId(data.execution_id);
      }

      let responseText;
      if (Array.isArray(data)) {
        const it = data.find(x => x?.output !== undefined);
        responseText = it?.output ?? (typeof data[0] === 'string' ? data[0] : JSON.stringify(data[0]));
      } else if (typeof data === 'object' && data) {
        responseText = data.output ?? data.response ?? data.message ?? JSON.stringify(data);
      } else if (typeof data === 'string') {
        responseText = data;
      } else {
        responseText = t('chatInterface.responseProcessingError', 'Received a response that could not be processed.');
      }

      const agentMessage = {
        id: Date.now() + 1,
        text: responseText,
        sender: 'agent',
        timestamp: new Date().toISOString()
      };

      const finalList = [...messagesRef.current, agentMessage];
      sessionStorage.setItem(`chat_${agent.id}_${user.id}`, JSON.stringify(finalList));

      if (mountedRef.current) {
        setMessages(finalList);
        setRetryCount(0);
        setChatState('idle');
      }

      database.saveInteraction({
        userId: user.id,
        agentName: agent.name,
        message: agentMessage.text,
        messageLength: agentMessage.text.length,
        category: 'agent_response'
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      console.error('sendToAgentOnce error:', err);
      
      const errorMsg = {
        id: Date.now() + 1,
        text: t('chatInterface.technicalDifficulty', 'Sorry, I\'m experiencing technical difficulties. Please try again.'),
        sender: 'agent',
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      const withError = [...messagesRef.current, errorMsg];
      sessionStorage.setItem(`chat_${agent.id}_${user.id}`, JSON.stringify(withError));
      
      if (mountedRef.current) {
        setMessages(withError);
        setChatState('idle');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setIsTyping(false);
      }
      
      inflightKeyRef.current = null;
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;
    
    const messageText = inputValue.trim();
    setInputValue('');
    setLoading(true);
    setIsTyping(true);
    autoScrollRef.current = true;

    const { updatedMessages } = enqueueUserMessage(messageText);

    database.saveInteraction({
      userId: user.id,
      agentName: agent.name,
      message: messageText,
      messageLength: messageText.length,
      category: 'user_message'
    });

    await logUserMessage({
      userId: user.id,
      agentId: agent.id,
      content: messageText
    });

    await sendToAgentOnce({ text: messageText });
  };

  const resetConversation = () => {
    if (abortRef.current) abortRef.current.abort();
    inflightKeyRef.current = null;

    const newSessionId = getSessionId(user.id, agent.id);
    setSessionId(newSessionId);

    const welcomeMessage = {
      id: Date.now(),
      text: `${t('chatInterface.welcome', 'Welcome to')} ${agent.name}! ${t('chatInterface.howCanIHelp', 'How can I help you today?')}`,
      sender: 'agent',
      timestamp: new Date().toISOString()
    };
    
    setMessages([welcomeMessage]);
    saveMessagesToSession([welcomeMessage]);

    localStorage.setItem(`session_${user.id}_${agent.id}`, newSessionId);

    autoScrollRef.current = true;
    setChatState('idle');
    setExecutionId(null);

    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleScrollToBottom = () => {
    autoScrollRef.current = true;
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const toggleAgentDetails = () => {
    setShowAgentDetails(!showAgentDetails);
  };

  const toggleRequestForm = () => {
    setShowRequestForm(!showRequestForm);
    if (!showRequestForm) {
      setRequestForm({
        name: user?.fullName || '',
        email: user?.email || '',
        subject: '',
        message: '',
        attachments: []
      });
      setFormErrors({});
      setRequestSuccess(false);
    }
  };

  const handleRequestFormChange = (e) => {
    const { name, value } = e.target;
    setRequestForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxSize = 10 * 1024 * 1024;
    
    if (totalSize > maxSize) {
      setFormErrors(prev => ({
        ...prev,
        attachments: t('chatInterface.attachmentsSizeError', 'Total file size cannot exceed 10MB')
      }));
      return;
    }
    
    setRequestForm(prev => ({
      ...prev,
      attachments: files
    }));
    
    if (formErrors.attachments) {
      setFormErrors(prev => ({
        ...prev,
        attachments: ''
      }));
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!requestForm.email) errors.email = t('chatInterface.emailRequired', 'Email is required');
    if (requestForm.email && !isValidEmail(requestForm.email)) errors.email = t('chatInterface.invalidEmail', 'Please enter a valid email');
    if (!requestForm.subject) errors.subject = t('chatInterface.subjectRequired', 'Subject is required');
    if (!requestForm.message) errors.message = t('chatInterface.messageRequired', 'Message is required');
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    const targetEmail = agentRequestEmail || defaultRequestEmail;
    
    try {
      console.log('Sending request to:', targetEmail, requestForm);
      
      const res = await fetch(import.meta.env.VITE_REQUEST_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestForm),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
           
      const timestamp = new Date().toISOString();
      const formattedDate = new Date().toLocaleString();
      
      const systemMessage = {
        id: Date.now(),
        text: t('chatInterface.requestSubmitted', 'Request submitted on {date}. A copy has been sent to {email}.').replace('{date}', formattedDate).replace('{email}'),
        sender: 'system',
        timestamp
      };
      
      const updatedMessages = [...messagesRef.current, systemMessage];
      persistAndSetIfMounted(updatedMessages);
      
      setRequestSuccess(true);
      
      setTimeout(() => {
        setShowRequestForm(false);
        setRequestSuccess(false);
        setRequestForm({
          name: user?.fullName || '',
          email: user?.email || '',
          subject: '',
          message: '',
          attachments: []
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting request:', error);
      setFormErrors({
        submit: t('chatInterface.submitRequestError', 'Failed to submit request. Please try again.')
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Button
              onClick={onBack}
              className="text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              icon={FiArrowLeft}
            />
            <div className={`w-10 h-10 rounded-lg ${agentImage ? 'bg-white' : 'bg-blue-500'} flex items-center justify-center text-white overflow-hidden`}>
              {agentImage ? (
                <img 
                  src={agentImage} 
                  alt={agent.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    setAgentImage(null);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                agent.icon
              )}
            </div>
            <div>
              <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
                {agent.name}
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                {isTyping ? t('chatInterface.typing', 'Typing...') : t('chatInterface.online', 'Online')}
              </p>
            </div>
          </div>
          <div className="flex flex-row lg:items-center space-x-2 gap-2">
            <Button
              onClick={toggleAgentDetails}
              className="flex items-center text-sm space-x-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
              title={t('chatInterface.agentDetails', 'Agent details')}
              icon={FiInfo}
              iconClassName="w-4 h-4"
            >
              {showAgentDetails ? t('chatInterface.hideDetails', 'Hide details') : t('chatInterface.showDetails', 'Show details')}
            </Button>
            <Button
              onClick={resetConversation}
              className="flex items-center text-sm space-x-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
              title={t('chatInterface.resetConversation', 'Reset conversation')}
              icon={FiRefreshCw}
              iconClassName='w-4 h-4'
            >
              {t('chatInterface.reset', 'Reset')}
            </Button>
          </div>
        </div>
        
        <AnimatePresence>
          {showAgentDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">{t('chatInterface.descriptionLabel', 'Description:')}</p>
                  <p className="text-gray-700 dark:text-gray-300">{agent.description || t('chatInterface.noDescription', 'No description provided')}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">{t('chatInterface.teamAssignmentLabel', 'Team Assignment:')}</p>
                  <p className="text-gray-700 dark:text-gray-300">{agent.team_assignment || t('chatInterface.availableToAllTeams', 'Available to all teams')}</p>
                </div>
                {hasPermission(user,"canCreateAgents") || hasPermission(user,"canEditAgents") ? (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">{t('chatInterface.webhookLabel', 'Webhook:')}</p>
                    <p className="text-gray-700 dark:text-gray-300 truncate">{agent.webhook}</p>
                  </div>
                ) : null}
              </div>
              <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                {t('chatInterface.sessionIdLabel', 'Session ID:')} {sessionId}
              </div>
            </motion.div>
          )}
        </AnimatePresence>        
      </div>

      {/* Messages Container */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4 scroll-smooth dark:bg-gray-900">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[90%] px-3 lg:px-4 py-2 rounded-lg relative group ${
                  message.sender === 'user'
                    ? 'bg-primary-500 text-white'
                    : message.sender === 'system'
                      ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-700'
                      : message.isError
                        ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-700'
                        : 'bg-white text-gray-800 border border-gray-200 dark:bg-gray-800 dark:text-white dark:border-gray-700'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap break-words">
                  {message.sender === 'agent' ? (
                    <div
                      className="markdown-message"
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(message.text, t) }}
                      ref={(el) => {
                        if (el) {
                          // Add copy buttons to code blocks after rendering
                          const codeBlocks = el.querySelectorAll('pre code');
                          codeBlocks.forEach((block) => {
                            if (block.parentNode.querySelector('.copy-btn')) return; // Skip if copy button already exists
                            const copyBtn = document.createElement('button');
                            copyBtn.className = 'copy-btn text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded';
                            copyBtn.textContent = t('chatInterface.copyButton', 'Copy');
                            copyBtn.onclick = () => {
                              navigator.clipboard.writeText(block.textContent).then(() => {
                                copyBtn.textContent = t('chatInterface.copiedButton', 'Copied!');
                                copyBtn.classList.add('bg-green-100', 'dark:bg-green-900', 'text-green-600', 'dark:text-green-400');
                                setTimeout(() => {
                                  copyBtn.textContent = t('chatInterface.copyButton', 'Copy');
                                  copyBtn.classList.remove('bg-green-100', 'dark:bg-green-900', 'text-green-600', 'dark:text-green-400');
                                }, 2000);
                              }).catch((err) => {
                                console.error('Failed to copy code:', err);
                              });
                            };
                            block.parentNode.insertBefore(copyBtn, block);
                          });
                        }
                      }}
                    />
                  ) : (
                    <p>{message.text}</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p
                    className={`text-xs ${
                      message.sender === 'user'
                        ? 'text-primary-100'
                        : message.sender === 'system'
                          ? 'text-yellow-500 dark:text-yellow-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {message.sender === 'agent' && (
                    <Button
                      onClick={() => handleCopyMessage(message.text, message.id)}
                      className="ml-2 p-1 rounded-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                      title={t('chatInterface.copyResponse', 'Copy response')}
                      icon={copiedMessageId === message.id ? FiCheck : FiCopy}
                      iconClassName={copiedMessageId === message.id ? 'w-4 h-4 text-green-600 dark:text-green-400' : 'w-4 h-4 text-gray-500 dark:text-gray-400'}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 px-3 lg:px-4 py-2 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} style={{ height: '1px', margin: 0, padding: 0 }}></div>
      </div>

      {!autoScrollRef.current && (
        <Button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={handleScrollToBottom}
          className="text-xs lg:text-sm fixed bottom-28 lg:bottom-20 right-4 lg:right-6 px-2 lg:px-3 py-1 lg:py-2 rounded-lg bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors z-10 flex items-center space-x-1 lg:space-x-2"
          icon={FiArrowDown}
          iconClassName='w-3 lg:w-4 h-3 lg:h-4'
        >
          {t('chatInterface.jumpToLatest', 'Jump to latest')}
        </Button>
      )}

      <Modal isOpen={showRequestForm} title={t('chatInterface.sendRequestTitle', 'Send a Request')} onClose={toggleRequestForm}>              
        {requestSuccess ? (
          <div className="p-4 lg:p-6 text-center">
            <div className="w-12 lg:w-16 h-12 lg:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheck} className="w-6 lg:w-8 h-6 lg:h-8 text-green-500" />
            </div>
            <h4 className="text-base lg:text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('chatInterface.requestSentSuccess', 'Request Sent Successfully!')}
            </h4>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('chatInterface.requestSentMessage', 'We\'ve received your request and will get back to you soon.')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('chatInterface.yourNameLabel', 'Your Name')}
              </label>
              <input
                type="text"
                name="name"
                value={requestForm.name}
                onChange={handleRequestFormChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('chatInterface.yourEmailLabel', 'Your Email *')}
              </label>
              <input
                type="email"
                name="email"
                value={requestForm.email}
                onChange={handleRequestFormChange}
                required
                className={`w-full px-3 py-2 border ${formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('chatInterface.subjectLabel', 'Subject *')}
              </label>
              <input
                type="text"
                name="subject"
                value={requestForm.subject}
                onChange={handleRequestFormChange}
                required
                className={`w-full px-3 py-2 border ${formErrors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
              />
              {formErrors.subject && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.subject}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('chatInterface.messageLabel', 'Message *')}
              </label>
              <textarea
                name="message"
                value={requestForm.message}
                onChange={handleRequestFormChange}
                required
                rows={4}
                className={`w-full px-3 py-2 border ${formErrors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm`}
              ></textarea>
              {formErrors.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('chatInterface.attachmentsLabel', 'Attachments (Optional, max 10MB total)')}
              </label>
              <div className="flex items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  appendClassName="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  icon={FiPaperclip}
                >
                  {t('chatInterface.attachFiles', 'Attach Files')}
                </Button>
              </div>
              
              {requestForm.attachments.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('chatInterface.filesSelected', '{count} file(s) selected').replace('{count}')}
                  </p>
                  <ul className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {Array.from(requestForm.attachments).map((file, index) => (
                      <li key={index}>
                        {file.name} ({Math.round(file.size / 1024)} KB)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {formErrors.attachments && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.attachments}</p>
              )}
            </div>
            
            {formErrors.submit && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700">
                {formErrors.submit}
              </div>
            )}
            
            <div className="flex flex-row justify-end pt-4 gap-2">
              <Button
                onClick={toggleRequestForm}
                className="px-3 lg:px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                {t('chatInterface.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                icon={FiMail}
              >
                {t('chatInterface.sendRequest', 'Send Request')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Input */}
      <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-row space-x-2 gap-2 max-w-12xl mx-auto">
          <Button
            onClick={toggleRequestForm}
            appendClassName="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            color="gray"
            icon={FiMail}
            responsive
          >
            {t('chatInterface.request', 'Request')}
          </Button>
          
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.repeat && inputValue.trim()) {
                e.preventDefault();
                handleSendMessage();
              }
              // Shift + Enter will add a new line (default behavior of textarea)
            }}
            placeholder={t('chatInterface.typeMessage', 'Type your message...')}
            disabled={loading}
            rows={1}
            className="flex-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 text-sm dark:bg-gray-700 dark:text-white resize-none"
            style={{ minHeight: '38px', maxHeight: '120px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
          />
          
          {chatState === 'executing' ? (
            <Button
              onClick={handleCancelExecution}
              appendClassName="bg-red-600 text-white rounded-lg hover:bg-red-700"
              icon={FiX}
              responsive
            >
              {t('chatInterface.cancel', 'Cancel')}
            </Button>
          ) : (
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || loading}
              loading={loading}
              icon={FiSend}
              responsive
            >
              {t('chatInterface.send', 'Send')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;